import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Button, CircularProgress } from '@mui/material';
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import ImersiveLogoDark from '../../assets/vertical-immersive-logo-dark.svg';
import {
  AuthFailureInfo,
  clearAuthFailure,
  readLastAuthFailure,
} from '../../providers/auth/auth-failure';
import AuthContext from '../../providers/auth/auth-service';
import { VenueProvider } from '../../providers/venue-provider';
import { WebSocketProvider } from '../../providers/websocket-context/websocket-provider';

const AuthLoading = () => (
  <div className="loading-container">
    <div>
      <img src={ImersiveLogoDark} alt="Immersive finance" className="logo" />
    </div>
    <div>
      <CircularProgress size={24} />
    </div>
  </div>
);

// Visible diagnostic shown when the Immersive session bootstrap is rejected
// (IM-2017) — replaces the previous silent bounce to Auth0. Surfaces WHY (e.g.
// "network/CORS — the auth host may be redirecting or blocking this origin") so
// a misconfigured auth env is diagnosable from the screen, not just the network
// tab. Auto-continues to login after a beat so nobody is stranded.
const AuthError = ({ info, onContinue }: { info: AuthFailureInfo; onContinue: () => void }) => (
  <div className="loading-container">
    <div>
      <img src={ImersiveLogoDark} alt="Immersive finance" className="logo" />
    </div>
    <div style={{ maxWidth: 420, textAlign: 'center' }}>
      <p style={{ fontWeight: 600, marginBottom: 8 }}>We couldn&apos;t sign you in</p>
      <p style={{ opacity: 0.8, marginBottom: 16 }}>{info.message}</p>
      <Button variant="contained" onClick={onContinue}>
        Return to login
      </Button>
    </div>
  </div>
);

// Grace period before auto-returning to Auth0 after a failure, so the reason is
// readable but no one is stranded on the error screen.
const AUTH_ERROR_REDIRECT_MS = 12000;

/**
 * IM-1991 fix #1: after a page refresh Auth0 restores the session but the
 * Immersive JWT (React state only) is gone. Re-run the SAME exchange as
 * /login/redirect instead of letting the app bounce to Auth0. Render a spinner
 * while exchanging; only send the user to Auth0 if the silent token / exchange
 * itself fails — and when it does, show the reason (IM-2017) rather than a
 * silent bounce.
 */
const ImmersiveSessionGate = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, logout } = useAuth0();
  const { accessToken, ensureImmersiveSession } = useContext(AuthContext);
  const attemptedRef = useRef(false);
  const [failure, setFailure] = useState<AuthFailureInfo | null>(null);

  const goToLogin = useCallback(() => {
    clearAuthFailure();
    logout({ logoutParams: { returnTo: import.meta.env.REACT_APP_AUTH0_LOGOUT } });
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated || accessToken || attemptedRef.current) {
      return;
    }
    attemptedRef.current = true;

    let cancelled = false;
    ensureImmersiveSession()
      .then((outcome) => {
        if (cancelled || outcome === 'ok') {
          return;
        }
        // Show the classified reason (recorded by the auth service) instead of
        // bouncing silently. Fall back to a generic message if nothing was
        // recorded (e.g. storage disabled).
        setFailure(
          readLastAuthFailure() ?? {
            step: 'unknown',
            kind: 'unknown',
            host: null,
            status: null,
            code: null,
            message: 'Your session could not be established. Please sign in again.',
          },
        );
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setFailure(
          readLastAuthFailure() ?? {
            step: 'unknown',
            kind: 'unknown',
            host: null,
            status: null,
            code: null,
            message: 'Your session could not be established. Please sign in again.',
          },
        );
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken]);

  // Auto-return to login after a grace period so the failure is never a dead end.
  useEffect(() => {
    if (!failure) {
      return;
    }
    const timer = setTimeout(goToLogin, AUTH_ERROR_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [failure, goToLogin]);

  if (failure) {
    return <AuthError info={failure} onContinue={goToLogin} />;
  }

  // Authenticated with Auth0 but no Immersive JWT yet: hold the app until the
  // re-exchange completes so WS / pages never mount with an empty token.
  if (isAuthenticated && !accessToken) {
    return <AuthLoading />;
  }

  return <>{children}</>;
};

export const ProtectedRoute = ({ component, returnUrl }: any) => {
  const returnTo = () => {
    return returnUrl || '/dashboard';
  };

  const Component = withAuthenticationRequired(component, {
    onRedirecting: AuthLoading,
    returnTo,
  });

  return (
    <WebSocketProvider>
      <VenueProvider>
        <ImmersiveSessionGate>
          <Component />
        </ImmersiveSessionGate>
      </VenueProvider>
    </WebSocketProvider>
  );
};
